/**
 * Brands Page - with Server-Side Pagination, Sorting and Selection
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataGrid } from '../../components/ui/DataGrid';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Plus, Tag, Edit, Trash2, Download, FileText, FileSpreadsheet, ChevronDown, CheckSquare, CheckCircle, XCircle } from 'lucide-react';

import BrandModal from './BrandModal';

export default function Brands() {
  const { t } = useTranslation();
  const gridRef = useRef(null);

  // Data state
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Server-side pagination state
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({});

  // Selection state
  const [selectedCount, setSelectedCount] = useState(0);

  // Quick filter state
  const [quickFilter, setQuickFilter] = useState(null);

  // Load brands with server-side params
  const loadBrands = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters object
      const apiFilters = {};
      if (filters.name) apiFilters.name = filters.name;
      if (filters.status && filters.status !== 'all') apiFilters.is_active = filters.status === 'active';

      // Apply quick filter
      if (quickFilter === 'active') apiFilters.is_active = true;
      if (quickFilter === 'inactive') apiFilters.is_active = false;

      const response = await api.post('/brands/list', {
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        filters: Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
      });

      if (response.data?.data?.items) {
        setBrands(response.data.data.items);
        setTotalItems(response.data.data.totalItems || 0);
      } else {
        setBrands([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error('Error loading brands:', err);
      toast.error('Erro ao carregar marcas');
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, sortBy, sortDirection, filters, quickFilter]);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // Handle page change from grid
  const handlePageChange = useCallback((newPage, newPageSize) => {
    setPageNumber(newPage);
    setPageSize(newPageSize);
  }, []);

  // Handle sort change from grid
  const handleSortChange = useCallback((newSortBy, newSortDirection) => {
    if (newSortBy && newSortDirection) {
      setSortBy(newSortBy);
      setSortDirection(newSortDirection);
    }
  }, []);

  // Handle filter search
  const handleSearch = useCallback(() => {
    setPageNumber(1);
    loadBrands();
  }, [loadBrands]);

  // Quick filter handler
  const handleQuickFilter = useCallback((filter) => {
    setQuickFilter(prev => prev === filter ? null : filter);
    setPageNumber(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setQuickFilter(null);
    setPageNumber(1);
  }, []);

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editingBrand?.id) {
        await api.put(`/brands/${editingBrand.id}`, data);
        toast.success('Marca atualizada com sucesso');
      } else {
        await api.post('/brands', data);
        toast.success('Marca criada com sucesso');
      }
      setModalOpen(false);
      setEditingBrand(null);
      await loadBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar marca');
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingBrand to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setEditingBrand(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/brands/${deleteId}`);
      toast.success('Marca excluída com sucesso');
      setDeleteId(null);
      await loadBrands();
    } catch (err) {
      toast.error('Erro ao excluir marca');
    }
  };

  // Export handlers
  const handleExportCSV = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    gridRef.current?.exportCSV(onlySelected);
  }, [selectedCount]);

  const handleExportJSON = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    const data = gridRef.current?.exportJSON(onlySelected);
    if (!data?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `marcas_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedCount]);

  // Track selection changes
  const handleSelectionChanged = useCallback(() => {
    const count = gridRef.current?.getSelectedCount() || 0;
    setSelectedCount(count);
  }, []);

  const filterDefinitions = useMemo(() => [
    {
      key: 'name',
      label: 'Nome',
      type: 'text',
      placeholder: 'Buscar marca...',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'active', label: 'Ativo' },
        { value: 'inactive', label: 'Inativo' },
      ],
    },
  ], []);

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 2,
      sortable: true,
      cellRenderer: (params) => (
        <span className="font-medium">{params.value}</span>
      ),
    },
    {
      field: 'description',
      headerName: 'Descrição',
      flex: 2,
      sortable: true,
    },
    {
      field: 'website',
      headerName: 'Website',
      flex: 1.5,
      sortable: true,
      cellRenderer: (params) => params.value ? (
        <a
          href={params.value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {params.value.replace(/^https?:\/\//, '')}
        </a>
      ) : '-',
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.8,
      sortable: true,
      cellRenderer: (params) => (
        <span className={params.value ? 'datagrid-status-active' : 'datagrid-status-inactive'}>
          {params.value ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      flex: 0.8,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className="datagrid-actions">
          <Button variant="ghost" size="icon" onClick={() => { setEditingBrand(params.data); setModalOpen(true); }}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteId(params.data.id)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ], []);

  // Custom export actions with selection option
  const ExportActions = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
          {selectedCount > 0 && (
            <Badge variant="secondary" className="ml-2">{selectedCount}</Badge>
          )}
          <ChevronDown className="h-3 w-3 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExportCSV(false)}>
          <FileText className="h-4 w-4 mr-2" />
          Exportar Tudo (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExportJSON(false)}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Exportar Tudo (JSON)
        </DropdownMenuItem>
        {selectedCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExportCSV(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (CSV)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportJSON(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (JSON)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="brands-page" data-testid="brands-page">
      <PageHeader
        title="Marcas"
        icon={Tag}
        subtitle={`${totalItems} marcas cadastradas`}
        actions={
          <div className="flex items-center gap-2">
            <ExportActions />
            <Button onClick={() => { setEditingBrand(null); setModalOpen(true); }} data-testid="new-brand-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nova Marca
            </Button>
          </div>
        }
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={setFilters}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        showExport={false}
      />

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          variant={quickFilter === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleQuickFilter('active')}
          className={quickFilter === 'active' ? '' : 'hover:bg-green-500/10'}
          data-testid="quick-filter-active"
        >
          <CheckCircle className="h-4 w-4 mr-1" />
          Ativos
        </Button>
        <Button
          variant={quickFilter === 'inactive' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleQuickFilter('inactive')}
          className={quickFilter === 'inactive' ? '' : 'hover:bg-gray-500/10'}
          data-testid="quick-filter-inactive"
        >
          <XCircle className="h-4 w-4 mr-1" />
          Inativos
        </Button>
        {quickFilter && (
          <Button variant="ghost" size="sm" onClick={() => setQuickFilter(null)}>
            Limpar filtro
          </Button>
        )}
      </div>

      <DataGrid
        ref={gridRef}
        title="Lista de Marcas"
        columnDefs={columnDefs}
        rowData={brands}
        loading={loading}
        enableSelection={true}
        getRowId={(params) => params.data.id}
        serverSide={true}
        totalItems={totalItems}
        pageNumber={pageNumber}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        sortBy={sortBy}
        sortDirection={sortDirection}
        paginationPageSize={pageSize}
        onSelectionChanged={handleSelectionChanged}
      />

      <BrandModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        brand={editingBrand}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta marca? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
